/**
 * @file PaperReviewPage.js
 * @module src/js/pages/memberZone/PaperReviewPage
 * @description
 * **Page component to display paper review materials uploaded by EDALab members.**
 * - Fetches paper review details from the `/api/paper-review` endpoint.
 * - Displays paper review date, title, and uploader in a paginated table.
 * - Allows members to upload, edit, or delete their own paper review materials.
 * - Includes a search bar to filter paper reviews by date, title, or uploader.
 * - Redirects to the login page if the user is not authenticated.
 *
 * @version
 * <p>
 *  2025-11-26: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-30: The "NEW" image added for news within 90 days. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useCheckAuthAndFetch hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useDeleteEntry. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "../../contexts/UserProvider";
import { useTheme } from "../../contexts/ThemeProvider";
import useCheckAuthAndFetch from "../../hooks/useCheckAuthAndFetch";
import useDeleteEntry from "../../hooks/useDeleteEntry";
import NumberPagination from "../../components/NumberPagination";
import PagedTable from "../../components/PagedTable";
import UploadForm from "../../components/UploadForm";
import SearchBar from "../../components/SearchBar";
import { TextWithBr, DownloadLink } from "../../components/UtilityComponents";
import SpanARow from "../../components/SpanARow";
import newGif from '../../../images/new.gif';

export default function PaperReviewPage() {
    const { pathname } = useLocation();
    const { uid } = useUser();
    const { theme } = useTheme();
    const checkAuthAndFetch = useCheckAuthAndFetch();
    const [papers, setPapers] = useState([]);
    const [filteredPapers, setFilteredPapers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentFirstPage, setCurrentFirstPage] = useState(1);
    const [updatedID, setUpdatedID] = useState(-1);
    const [deleting, setDeleting] = useState(false);
    const [loading, setLoading] = useState(true);
    const headerRef = useRef(null);
    const deleteEntry = useDeleteEntry({ setData: setPapers, setDeleting });

    const numPageButtons = 10;
    const numItemsPerPage = 15;

    const papersOnCurrentPage = useMemo(() => {
        if (!filteredPapers || filteredPapers.length === 0) return [];
        const start = (currentPage - 1) * numItemsPerPage;
        const end = currentPage * numItemsPerPage;
        return filteredPapers.slice(start, end);
    }, [filteredPapers, currentPage]);

    useEffect(() => {
        async function checkAuthAndFetchData() {
            const data = await checkAuthAndFetch({ request: "/api/paper-review" });
            setPapers(data || []);
            setLoading(false);
        }
        checkAuthAndFetchData();
    }, [checkAuthAndFetch]);

    const pagination = (
        <NumberPagination
            length={papers.length}
            numPageButtons={numPageButtons}
            numItemsPerPage={numItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            currentFirstPage={currentFirstPage}
            setCurrentFirstPage={setCurrentFirstPage}
            headerRef={headerRef}
        />
    )

    // The reason for using useMemo here is to prevent unnecessary re-renders
    // You can find more details in the News.js component
    const filterKeys = useMemo(() => ['date', 'title', 'uploader'], []);
    const searchBar = (
        <SearchBar
            data={papers}
            setFilteredData={setFilteredPapers}
            setCurrentPage={setCurrentPage}
            setCurrentFirstPage={setCurrentFirstPage}
            filterKeys={filterKeys}
        />
    );

    const table = (
        <div className="table-wrapper">
            <table>
                <thead>
                    <tr>
                        <th className='hori-vert-center' width='15%'>Date</th>
                        <th className='hori-vert-center'>Title</th>
                        <th className='hori-vert-center' width='20%'>Uploader</th>
                    </tr>
                </thead>
                <tbody>
                    <UploadForm
                        request='/api/paper-review'
                        acceptList={['.pdf', '.pptx']}
                        setData={setPapers}
                        text='📃 Upload materials for paper review (.pdf and .pptx)'
                        from={pathname}
                    />
                    <UploadForm
                        request='/api/paper-review'
                        acceptList={['.zip']}
                        setData={setPapers}
                        text='📃 Upload materials for paper review (.zip)'
                        from={pathname}
                    />
                    {filteredPapers.length === 0 && (
                        <SpanARow
                            colSpan={3}
                            text="No papers found"
                            loading={loading}
                        />
                    )}
                    {papersOnCurrentPage.map((item, i) => (
                        item.meeting_ID !== updatedID ? (
                            <tr key={i}>
                                <td className='hori-vert-center'>
                                    {item.date}<br />
                                    {new Date(item.date) >= new Date(Date.now() - 90 * 24 * 60 * 60 * 1000) && (
                                        <img src={newGif} alt='New' style={{ marginLeft: '5px' }} />
                                    )}
                                </td>
                                <td style={{textAlign: 'left', verticalAlign: 'middle'}}>
                                    <span>{item.title}<br /></span>
                                    <span style={{ fontSize: '0.9em', color: theme === 'dark' ? 'lightgray' : 'gray' }}>
                                        {item.paper_file.endsWith('.zip') && item.slide_file.endsWith('.zip') ? (
                                            <DownloadLink file={item.paper_file}>
                                                Papers and Slides
                                            </DownloadLink>
                                        ) : (
                                            <>
                                                <DownloadLink file={item.paper_file}>
                                                    Paper
                                                </DownloadLink>&nbsp;&nbsp;&nbsp;
                                                <DownloadLink file={item.slide_file}>
                                                    Slides
                                                </DownloadLink>
                                            </>
                                        )}
                                    </span>
                                </td>
                                <td className='hori-vert-center'>
                                    {item.present_ID === uid ? (
                                        <>
                                            <button
                                                onClick={() => setUpdatedID(item.meeting_ID)}
                                                disabled={deleting}
                                                style={{ marginBottom: '15px' }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => deleteEntry({
                                                    request: "/api/paper-review",
                                                    id: item.meeting_ID,
                                                    from: pathname,
                                                })}
                                                disabled={deleting}
                                                style={{
                                                    color: theme === 'dark' ? 'lightcoral' : 'red',
                                                    outline: `2px solid ${theme === 'dark' ? 'lightcoral' : 'rgba(255, 0, 0, 0.1)'}`,
                                                    outlineOffset: '-2px',
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </>
                                    ) : <TextWithBr text={item.uploader} />}
                                </td>
                            </tr>
                        ) : (
                            <UploadForm
                                key={i}
                                request="/api/paper-review"
                                acceptList={item.paper_file.endsWith('.zip') && item.slide_file.endsWith('.zip') ?
                                    ['.zip'] : ['.pdf', '.pptx']
                                }
                                setData={setPapers}
                                item={item}
                                updatedID={updatedID}
                                setUpdatedID={setUpdatedID}
                                from={pathname}
                            />
                        )
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <PagedTable
            header="Paper Review"
            headerRef={headerRef}
            numberPagination={filteredPapers.length > numItemsPerPage ? pagination : null}
            table={table}
            searchBar={searchBar}
        />
    )
}
